import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Factory, Loader2 } from "lucide-react";

export default function Register() {
  const { user, register, error } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "planner" });
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await register(form);
    setBusy(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-[var(--bg-canvas)] p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center">
            <Factory className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Create your account</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em]">Join the plant team</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-slate-500">Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-slate-500">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-slate-500">Password</Label>
            <PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="register-password-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-slate-500">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="register-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planner">Planner</SelectItem>
                <SelectItem value="operator">Operator</SelectItem>
                <SelectItem value="quality">Quality</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error ? <div className="text-sm text-red-600" data-testid="register-error">{error}</div> : null}
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="register-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Create account
          </Button>
        </form>

        <div className="mt-6 text-sm text-slate-500 text-center">
          Already registered?{" "}
          <Link to="/login" className="text-blue-700 font-medium" data-testid="back-to-login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
